import {
    Alert,
    Button,
    Checkbox,
    Divider,
    Group,
    Loader,
    SegmentedControl,
    Stack,
    Text,
    Textarea,
    ThemeIcon,
    Title,
    Tooltip,
} from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { useField, UseFormReturnType } from "@mantine/form";
import { modals } from "@mantine/modals";
import { IconInfoCircle, IconInfoTriangle, IconMailFast } from "@tabler/icons-react";
import { queryOptions, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { Suspense, useState } from "react";
import { httpPostGraphql } from "shared/api/httpClient";
import { Section, SectionProvider } from "shared/components/global/Section";
import {
    ComposeFormProvider,
    ComposeFormState,
    composeFormStateToDraftInfoInput,
    useComposeFormContext,
} from "shared/components/message/compose/context";
import { DurationEditor } from "shared/components/message/compose/DurationEditor";
import { useIsInBlackoutPeriod } from "shared/graphql/cust";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { useIsSupport } from "shared/hooks/useIsSupport";
import { arr } from "shared/utils/array";
import { tomorrow } from "shared/utils/date";
import { to, voiding } from "shared/utils/fns";
import { maybe } from "shared/utils/maybe";
import { notify } from "shared/utils/notify";
import { qk } from "shared/utils/qk";
import { zInstant } from "shared/utils/zod";
import { graphql } from "src/gql";
import { Cm, DurationInfoInput } from "src/gql/graphql";
import { match } from "ts-pattern";

export function useMessageActions() {
    const { isSupport } = useIsSupport();
    const createNote = useGraphqlMutation(muts.createNote);
    const unschedule = useGraphqlMutation(muts.unschedule);

    return {
        async openCreateNoteModal(opts: { id: string; content: string }) {
            const { close } = await notify.confirm(
                "Are you sure you want to publish this note? It will be visible to all recipients of the message.",
                {
                    color: "primary",
                    title: "Create Note",
                    label: "Publish",
                },
            );

            await createNote.mutateAsync(opts);
            notify.show.success("Note created.");
            await qk.invalidate("messages", "notes");
            close();
        },

        async cancelScheduledDelivery(id: string) {
            const { close } = await notify.confirm(
                "Are you sure you want to cancel the delivery of this message? It will still be accessible as a draft.",
                { color: "brand", title: "Cancel Schedule" },
            );
            await unschedule.mutateAsync({ id });
            notify.show.success("Schedule canceled.");
            await qk.invalidate("messages", "draft");
            await qk.invalidate("messages", "scheduled");
            close();
        },

        openPreviewModal(opts: { form: UseFormReturnType<ComposeFormState> }) {
            if (isSupport) {
                notify.show.warn({ message: "Operation not permitted for support." });
                return;
            }

            modals.open({
                title: <Title order={5}>Send Preview</Title>,
                children: (
                    <ComposeFormProvider form={opts.form}>
                        <Suspense fallback={<Loader />}>
                            <SendPreviewModal />
                        </Suspense>
                    </ComposeFormProvider>
                ),
            });
        },
        openUpdateStatusModal(msgId: string) {
            function Panel() {
                const updateDuration = useGraphqlMutation(muts.updateDuration);
                const expireMessage = useGraphqlMutation(muts.expireMessage);

                const query = useQuery({
                    queryKey: qk("messages", "duration", msgId),
                    select: to("message"),
                    queryFn: () =>
                        httpPostGraphql(
                            graphql(`
                                query MessageActionUpdateDuration($id: UUID!) {
                                    message(id: $id) {
                                        id
                                        expireAt
                                    }
                                }
                            `),
                            { id: msgId },
                        ),
                });
                const [note, setNote] = useState("");
                const [duration, setDuration] = useState<DurationInfoInput>({ absolute: (+tomorrow()).toString() });
                const [tab, setTab] = useState("Expire Now");
                const [error, setError] = useState<string | null>(null);

                return (
                    <Stack>
                        <Text size="xs" c="dimmed" mt="-sm" style={{ zIndex: 1e3 }}>
                            This message is currently set to
                            {maybe(query.data?.expireAt)
                                ?.map(parseInt)
                                .take((it) => " expire at " + new Date(it).toLocaleString()) ?? " never expire."}
                        </Text>
                        <SegmentedControl data={["Expire Now", "Expire Later"]} value={tab} onChange={setTab} />
                        {tab == "Expire Later" && (
                            <DurationEditor
                                value={duration}
                                minDate={new Date()}
                                onChange={(value) => {
                                    setDuration(value);
                                    if (error) setError(null);
                                }}
                                error={error ?? undefined}
                            />
                        )}
                        <Textarea placeholder="Add a note..." minRows={2} autosize value={note} onChange={(e) => setNote(e.target.value)} />
                        <Divider mx="-md" />
                        <Group justify="flex-end">
                            <Button variant="default" onClick={() => modals.closeAll()}>
                                Cancel
                            </Button>
                            <Button
                                loading={!updateDuration.isIdle}
                                disabled={!updateDuration.isIdle || !duration}
                                onClick={() => {
                                    if (!duration) return;
                                    if (tab == "Expire Later" && duration.absolute && new Date(+duration.absolute) <= new Date()) {
                                        setError("Expiration must be in the future.");
                                        return;
                                    }

                                    match(tab)
                                        .with("Expire Later", () =>
                                            updateDuration.mutateAsync({
                                                id: msgId,
                                                duration,
                                                note: maybe(note.trim()).takeIf((it) => !!it),
                                            }),
                                        )
                                        .with("Expire Now", () =>
                                            expireMessage.mutateAsync({ id: msgId, note: maybe(note.trim()).takeIf((it) => !!it) }),
                                        )
                                        .run()
                                        .then(() => {
                                            modals.closeAll();
                                            notify.show.success("Message duration updated successfully.");

                                            // i don't want to hear it...
                                            // we have to do this beacuse expireMessage queues a quartz job to execute immediately, but
                                            // it's possible we'd refetch our queries before that job was fired.
                                            // at some point we need to finally bite the bullet and do websocket updates...
                                            setTimeout(() => {
                                                void qk.invalidate("messages");
                                            }, 1000);
                                        })
                                        .catch(notify.catch);
                                }}
                            >
                                Done
                            </Button>
                        </Group>
                    </Stack>
                );
            }

            modals.open({
                title: <Title order={3}>Update Message Status</Title>,
                children: <Panel />,
            });
        },
        openScheduleDeliveryModal(opts: {
            getMessage: () => Promise<{ id: string }>;
            isDisabled?: () => string | null;
            initialValue?: Date;
        }): Promise<{ id: string }> {
            const { promise, resolve, reject } = Promise.withResolvers<{ id: string }>();
            const disabledMessage = opts.isDisabled?.() ?? null;

            const Panel = () => {
                const sendAt = useField({ initialValue: opts.initialValue ?? tomorrow() });
                const scheduleDelivery = useGraphqlMutation(muts.scheduleDelivery);
                const { isInBlackoutPeriod } = useIsInBlackoutPeriod(sendAt.getValue());

                async function handleSchedule() {
                    await notify.confirm("Are you sure you want to schedule this message?", {
                        title: "Schedule Message",
                    });

                    const { id } = await opts.getMessage();
                    await scheduleDelivery.mutateAsync({ id, sendAt: zInstant.parse(sendAt.getValue()) });
                    notify.show.success("Message scheduled.");

                    await qk.invalidate("messages", "draft");
                    await qk.invalidate("messages", "scheduled");

                    resolve({ id });
                    modals.closeAll();
                }

                return (
                    <Stack>
                        <DateTimePicker
                            label="Scheduled Time"
                            valueFormat="MMM DD YYYY hh:mm A"
                            timePickerProps={{
                                withDropdown: true,
                                popoverProps: { withinPortal: false },
                                format: "12h",
                            }}
                            presets={[
                                { value: dayjs().add(1, "day").format("MMM DD YYYY hh:mm A"), label: "Tomorrow" },
                                { value: dayjs().add(1, "week").format("MMM DD YYYY hh:mm A"), label: "Next Week" },
                                { value: dayjs().add(1, "month").format("MMM DD YYYY hh:mm A"), label: "Next Month" },
                                { value: dayjs().endOf("week").format("MMM DD YYYY hh:mm A"), label: "End of the Week" },
                                { value: dayjs().endOf("month").format("MMM DD YYYY hh:mm A"), label: "End of the Month" },
                            ]}
                            {...sendAt.getInputProps()}
                        />

                        {isInBlackoutPeriod && (
                            <Alert variant="light" icon={<IconInfoTriangle size={20} />} title="Blackout Period">
                                You are scheduling a message during your organization's blackout period.
                            </Alert>
                        )}

                        <Group justify="space-between">
                            {isInBlackoutPeriod ? (
                                <Tooltip label={disabledMessage} disabled={disabledMessage == null} maw={400}>
                                    <Button
                                        onClick={voiding(handleSchedule)}
                                        disabled={disabledMessage != null}
                                        size="compact-sm"
                                        variant="transparent"
                                        td="underline"
                                    >
                                        Schedule anyway
                                    </Button>
                                </Tooltip>
                            ) : (
                                <div />
                            )}
                            <Group>
                                <Button variant="default" onClick={() => modals.closeAll()}>
                                    Cancel
                                </Button>
                                <Tooltip label={disabledMessage} disabled={disabledMessage == null} maw={400}>
                                    <Button
                                        disabled={isInBlackoutPeriod || disabledMessage != null}
                                        onClick={voiding(handleSchedule)}
                                        loading={!scheduleDelivery.isIdle}
                                    >
                                        Schedule
                                    </Button>
                                </Tooltip>
                            </Group>
                        </Group>
                    </Stack>
                );
            };

            modals.open({
                title: <Title order={3}>Schedule for Later</Title>,
                children: <Panel />,
                onClose() {
                    reject();
                },
            });

            return promise;
        },
    };
}

const SendPreviewModal = () => {
    const { data: availableCms } = useSuspenseQuery(queries.availableCms);
    const [cms, setCms] = useState(availableCms);

    const form = useComposeFormContext();

    const preview = useGraphqlMutation({
        document: muts.preview,
        onSettled: modals.closeAll,
        onSuccess() {
            notify.show.success("Preview sent.");
        },
    });

    function handleChange(cm: Cm, checked: boolean) {
        setCms((prev) => (checked ? prev.concat([cm]) : arr(prev).except(cm).take()));
    }

    function handleSend() {
        preview.mutate({
            info: composeFormStateToDraftInfoInput.parse(form.getValues()),
            cms,
        });
    }

    return (
        <Stack>
            <Text size="sm">
                Preview messages will only be sent to you, via the communication methods selected below. The message will not be shown in
                the sent folder.
            </Text>

            <SectionProvider>
                <Section label="Send As">
                    <SendPreviewCmCheckbox repr={Cm.Sms} label="SMS" cms={cms} onChange={(checked) => handleChange(Cm.Sms, checked)} />
                    <SendPreviewCmCheckbox
                        repr={Cm.Phone}
                        label="Phone Call"
                        cms={cms}
                        onChange={(checked) => handleChange(Cm.Phone, checked)}
                    />
                    <SendPreviewCmCheckbox
                        repr={Cm.Email}
                        label="Email"
                        cms={cms}
                        onChange={(checked) => handleChange(Cm.Email, checked)}
                    />
                </Section>

                <Divider mb="md" />

                <Group justify="flex-end">
                    <Button variant="default" onClick={modals.closeAll}>
                        Cancel
                    </Button>
                    <Button
                        leftSection={<IconMailFast size={16} />}
                        loading={preview.isPending}
                        onClick={handleSend}
                        disabled={!composeFormStateToDraftInfoInput.safeParse(form.getValues()).success}
                    >
                        Send
                    </Button>
                </Group>
            </SectionProvider>
        </Stack>
    );
};

const SendPreviewCmCheckbox = (props: { repr: Cm; label: string; cms: Cm[]; onChange: (checked: boolean) => unknown }) => {
    const { data: availableCms } = useSuspenseQuery(queries.availableCms);

    return (
        <Group gap={2}>
            <Checkbox
                disabled={!availableCms.includes(props.repr)}
                checked={props.cms.includes(props.repr)}
                label={props.label}
                onChange={(e) => props.onChange(e.target.checked)}
            />
            {!availableCms.includes(props.repr) && (
                <Tooltip label="You do not have a device configured for this communication method.">
                    <ThemeIcon c="dimmed" variant="transparent">
                        <IconInfoCircle size={16} />
                    </ThemeIcon>
                </Tooltip>
            )}
        </Group>
    );
};

const queries = {
    availableCms: queryOptions({
        queryKey: ["composeActions", "availableCms"],
        select: ({ self: { cms } }) => cms.filter((it) => [Cm.Sms, Cm.Phone, Cm.Email].includes(it)),
        queryFn: () =>
            httpPostGraphql(
                graphql(`
                    query ComposeActionPreviewCMs {
                        self {
                            cms
                        }
                    }
                `),
                {},
            ),
    }),
};

const muts = {
    expireMessage: graphql(`
        mutation MessageActionsExpireMessage($id: UUID!, $note: String) {
            expireMessage(id: $id, note: $note) {
                id
            }
        }
    `),
    updateDuration: graphql(`
        mutation MessageActionsUpdateDuration($id: UUID!, $duration: DurationInfoInput!, $note: String) {
            rescheduleMessageExpiration(id: $id, duration: $duration, note: $note) {
                id
            }
        }
    `),
    scheduleDelivery: graphql(`
        mutation MessageActionsScheduleDelivery($id: UUID!, $sendAt: Instant!) {
            scheduleMessage(id: $id, sendAt: $sendAt) {
                id
            }
        }
    `),
    unschedule: graphql(`
        mutation MessageActionsCancelScheduledDelivery($id: UUID!) {
            unscheduleMessage(id: $id) {
                id
            }
        }
    `),
    preview: graphql(`
        mutation ComposeActionPreview($info: DraftInfoInput!, $cms: [CM!]!) {
            sendPreview(params: $info, cms: $cms) {
                id
            }
        }
    `),
    createNote: graphql(`
        mutation ComposeActionCreateNote($id: UUID!, $content: String!) {
            createNote(id: $id, locales: [{ language: ENGLISH, richBody: $content, body: $content, subject: "NOTE" }]) {
                id
            }
        }
    `),
};
